import { useState, useEffect } from 'react';

/**
 * Custom hook for creating typewriter effect
 * @param {string} text - The text to type out
 * @param {number} speed - Typing speed in milliseconds per character
 * @param {number} delay - Delay before starting to type (in milliseconds)
 * @param {boolean} loop - Whether to loop the animation
 * @param {number} pauseTime - Pause time at the end before restarting (if loop is true)
 * @returns {object} - { displayText, isTyping, isComplete }
 */
const useTypewriter = (text, speed = 100, delay = 0, loop = false, pauseTime = 2000) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!text) return;

    let timeoutId;
    let currentIndex = 0;
    let isDeleting = false;

    const typeText = () => {
      if (isDeleting) {
        // Deleting characters
        setDisplayText(text.substring(0, currentIndex - 1));
        currentIndex--;
        
        if (currentIndex === 0) {
          isDeleting = false;
          timeoutId = setTimeout(typeText, pauseTime);
        } else {
          timeoutId = setTimeout(typeText, speed / 2); // Faster when deleting
        }
      } else {
        // Typing characters
        setDisplayText(text.substring(0, currentIndex + 1));
        currentIndex++;
        setIsTyping(true);
        
        if (currentIndex === text.length) {
          setIsComplete(true);
          setIsTyping(false);
          
          if (loop) {
            timeoutId = setTimeout(() => {
              isDeleting = true;
              setIsComplete(false);
              typeText();
            }, pauseTime);
          }
        } else {
          timeoutId = setTimeout(typeText, speed);
        }
      }
    };

    // Start the animation after delay
    timeoutId = setTimeout(typeText, delay);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [text, speed, delay, loop, pauseTime]);

  return { displayText, isTyping, isComplete };
};

export default useTypewriter;
